import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { OrderState } from 'state/secondaryMarket/cpOrder/order/order.model';
import type { QuoteState } from 'state/secondaryMarket/live/quote/liveQuote.model';
import type { LiveTradeState } from 'state/secondaryMarket/live/trade/trade.model';
import type { SecondaryMarketQuoteRtForm } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm';
import type { TradeState } from 'state/secondaryMarket/realtimeQuote/trade';
import { getSecondaryMarketDlpProductFromTab } from 'state/secondaryMarket/referential/dlpProduct/dlpProduct.selectors';
import type { AmendedTrade } from 'state/secondaryMarket/shared/amend/amend.model';
import type { AppState } from 'state/state.model';
import { SecondaryMarketPostEventState } from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.model';
import type { SecondaryMarketQuotesState } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import type { WorkspaceTab } from 'state/workspaces/tabs/tabs.model';
import { getTabType, getWorkspaceTab } from 'state/workspaces/tabs/tabs.selector';

export interface SecondaryMarketProductInfo {
  isin?: string;
  fileCode?: string;
  key?: string;
}

function selectQuote(
  state: AppState,
  tabId: string,
):
  | SecondaryMarketQuoteRtForm
  | QuoteState
  | (AmendedTrade & SecondaryMarketPostEventState & SecondaryMarketQuotesState & WorkspaceTab)
  | undefined {
  const live = (state.secondaryMarket?.live?.quotes ?? {})[tabId];

  const realtimeQuote = (state.secondaryMarket?.realtimeQuote?.form ?? {})[tabId];
  return realtimeQuote ?? live ?? undefined;
}

function selectAmendQuote(state: AppState, tabId: string): AmendedTrade | undefined {
  const amendLiveSecondaryMarket = (state.secondaryMarket?.live?.amend ?? {})[tabId];
  const amendLiveTab = (state.workspaces?.tabs ?? {})[tabId];
  const amendLiveWorkspace =
    (state.workspaces?.secondaryMarketQuotes ?? {})[tabId] ??
    (state.workspaces?.secondaryMarketPostEvents ?? {})[tabId];
  const amendLive = { ...amendLiveTab, ...amendLiveSecondaryMarket, ...amendLiveWorkspace };
  return amendLive;
}

function selectTrade(state: AppState, tabId: string): TradeState | LiveTradeState | undefined {
  const live = (state.secondaryMarket?.live?.trades ?? {})[tabId];
  const realtimeQuote = (state.secondaryMarket?.realtimeQuote?.trade ?? {})[tabId];
  return realtimeQuote ?? live ?? undefined;
}

function selectOrder(state: AppState, tabId: string): OrderState | undefined {
  const cpOrder = (state.secondaryMarket?.cpOrder?.order ?? {})[tabId];
  const limitOrder = (state.secondaryMarket?.limitOrder?.order ?? {})[tabId];
  return cpOrder ?? limitOrder ?? undefined;
}

function selectAmendCpOrder(state: AppState, tabId: string): AmendedTrade | undefined {
  const cpOrder = (state.secondaryMarket?.cpOrder?.amend ?? {})[tabId];
  return cpOrder ?? undefined;
}

function selectAmendLimitOrder(state: AppState, tabId: string): AmendedTrade | undefined {
  const limitOrder = (state.secondaryMarket?.limitOrder?.amend ?? {})[tabId];
  return limitOrder ?? undefined;
}

function selectEbizProductInfo(state: AppState, tabId: string): SecondaryMarketProductInfo {
  const ebizProductKey = (state.workspaces?.secondaryMarketQuotes ?? {})[tabId]?.key;
  if (!ebizProductKey) {
    return { isin: undefined, fileCode: undefined, key: undefined };
  }

  const ebizProduct = (state.secondaryMarket?.referential?.astroProduct ?? {})[ebizProductKey.replace('ISIN::', '')];
  const ebizProductCode = `${ebizProduct?.data?.productIdSource}:ID_${ebizProduct?.data?.productIdType}:${ebizProduct?.data?.productId}`;

  return {
    isin: ebizProduct?.data?.isin,
    fileCode: ebizProduct?.data?.fileCode,
    key: ebizProductCode,
  };
}

function buildProductKeyOnPostEventsTab(state: AppState, tabId: string): string | undefined {
  const bcsProductKey = (state.workspaces?.secondaryMarketPostEvents ?? {})[tabId]?.bcsProductKey;
  return bcsProductKey ? `${bcsProductKey.source}:${bcsProductKey.subType}:${bcsProductKey.code}` : undefined;
}

function selectProductInfo(state: AppState, tabId: string) {
  const tabType = getTabType(state, tabId);
  if (tabType === 'dlpProducts') {
    const dlpTab = getWorkspaceTab(state, tabId);
    const dlpProduct = getSecondaryMarketDlpProductFromTab(state, tabId);
    return {
      isin: dlpProduct?.data?.isin,
      key: dlpTab?.key,
    };
  }
  const cpOrderProduct = (state.secondaryMarket?.cpOrder?.referential?.product ?? {})[tabId];
  const limitOrderProduct = (state.secondaryMarket?.limitOrder?.referential ?? {})[tabId];
  const liveProduct = (state.secondaryMarket?.live?.referential ?? {})[tabId];
  const ebizProductInfo = selectEbizProductInfo(state, tabId);

  const postEventsBcsProductKey = buildProductKeyOnPostEventsTab(state, tabId);
  const bcsProductKey =
    (state.workspaces?.tabs ?? {})[tabId]?.key ??
    (state.workspaces?.secondaryMarketQuotes ?? {})[tabId]?.key ??
    postEventsBcsProductKey;

  const productCode = bcsProductKey?.startsWith('ISIN::') ? ebizProductInfo?.key : bcsProductKey;

  const isin =
    cpOrderProduct?.bcsProduct?.isin ??
    limitOrderProduct?.isin ??
    liveProduct?.isin ??
    ebizProductInfo.isin ??
    undefined;

  const fileCode =
    cpOrderProduct?.bcsProduct?.fileCode ??
    limitOrderProduct?.fileCode ??
    liveProduct?.fileCode ??
    ebizProductInfo.fileCode ??
    undefined;

  const key = productCode;

  return { isin, fileCode, key };
}

export function getSecondaryMarketQuoteDetails(
  state: AppState,
  tabId: string,
):
  | SecondaryMarketQuoteRtForm
  | QuoteState
  | (AmendedTrade & SecondaryMarketPostEventState & SecondaryMarketQuotesState & WorkspaceTab)
  | undefined {
  const quote = selectQuote(state, tabId);
  return quote;
}
export function getSecondaryMarketOrderDetails(state: AppState, tabId: string): OrderState | undefined {
  const order = selectOrder(state, tabId);
  return order;
}
export function getSecondaryMarketQuoteRejections(state: AppState, tabId: string) {
  const live = selectQuote(state, tabId);
  const amendLive = selectAmendQuote(state, tabId);
  const order = selectOrder(state, tabId);
  const amendCpOrder = selectAmendCpOrder(state, tabId);
  const amendLimitOrder = selectAmendLimitOrder(state, tabId);
  const workflowErrors: NextWorkflowError[] = [];

  if (order?.errorMessage) {
    workflowErrors.push({
      rejectionMessage: order?.errorMessage,
    });
  }

  if (amendCpOrder?.errorMessage) {
    workflowErrors.push({
      rejectionMessage: amendCpOrder?.errorMessage,
    });
  }

  if (amendLimitOrder?.errorMessage) {
    workflowErrors.push({
      rejectionMessage: amendLimitOrder?.errorMessage,
    });
  }

  if (live?.errorMessage) {
    workflowErrors.push({
      rejectionMessage: live?.errorMessage,
    });
  }

  if (amendLive?.errorMessage) {
    workflowErrors.push({
      rejectionMessage: amendLive?.errorMessage,
    });
  }

  return workflowErrors;
}

export function getSecondaryMarketTradeRejections(state: AppState, tabId: string) {
  const live = selectTrade(state, tabId);
  const workflowErrors: NextWorkflowError[] = [];

  if (live?.errorMessage) {
    workflowErrors.push({
      rejectionMessage: live?.errorMessage,
    });
  }
  return workflowErrors;
}

export function getSecondaryMarketIsinCode(state: AppState, tabId: string): string | undefined {
  const orderIsinCode = selectProductInfo(state, tabId).isin;
  return orderIsinCode;
}

export function getSecondaryMarketFileCode(state: AppState, tabId: string): string | undefined {
  const fileCode = selectProductInfo(state, tabId).fileCode;
  return fileCode;
}

export function getSecondaryMarketProductKey(state: AppState, tabId: string): string | undefined {
  const productKey = selectProductInfo(state, tabId).key;
  return productKey;
}
